import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeTranslationsSchema } from '../../common/translations/translations';

import { SettingScopeFieldsSchema, refineSettingScope } from '../setting-scope/setting-scope';

/**
 * A translatable setting's value: one string per locale (N5, U8 parity).
 * `brand.name` is the canonical case — the operator writes both, or neither.
 */
export const SettingValueTranslationsSchema = makeTranslationsSchema(z.string().min(1).max(2000));

export type SettingValueTranslations = z.infer<typeof SettingValueTranslationsSchema>;

/**
 * One **stored** row: this key, at this scope, has this value.
 *
 * `value` and `translations` are mutually exclusive by construction — a
 * translatable definition stores its content in the translations table and
 * leaves the jsonb column null.
 */
export const SettingValueSchema = SettingScopeFieldsSchema.extend({
  key: z.string().min(1),
  value: z.unknown(),
  translations: SettingValueTranslationsSchema.nullable().default(null),
  updatedAt: z.string(),
  /** Null when the writer was the system (a seed), not a person. */
  updatedByUserId: UuidSchema.nullable(),
})
  .strict()
  .superRefine(refineSettingScope);

export type SettingValueDto = z.infer<typeof SettingValueSchema>;

/**
 * Which layer of the resolution chain answered — `value(scope)` → `value(global)`
 * → coded default (U9). The admin shows it verbatim ("inherited from global",
 * "default: 10"), which is what makes an override's effect legible (W8).
 */
export const SETTING_VALUE_SOURCES = ['scoped', 'global', 'default'] as const;

export const SettingValueSourceSchema = z.enum(SETTING_VALUE_SOURCES);

export type SettingValueSource = (typeof SETTING_VALUE_SOURCES)[number];

/** What a given scope actually resolves to right now — the effective-value preview. */
export const SettingEffectiveValueSchema = SettingScopeFieldsSchema.extend({
  key: z.string().min(1),
  value: z.unknown(),
  source: SettingValueSourceSchema,
})
  .strict()
  .superRefine(refineSettingScope);

export type SettingEffectiveValueDto = z.infer<typeof SettingEffectiveValueSchema>;

/**
 * One key in full: every stored scope row plus what each allowed scope
 * currently resolves to. The settings screen renders a row from this without a
 * second request per scope.
 */
export const SettingDetailSchema = z
  .object({
    key: z.string().min(1),
    values: z.array(SettingValueSchema),
    effective: z.array(SettingEffectiveValueSchema),
  })
  .strict();

export type SettingDetailDto = z.infer<typeof SettingDetailSchema>;

/**
 * Many keys in full — what a settings group resolves to in ONE request.
 *
 * An object rather than a bare array so the response can grow a `meta` without
 * breaking every consumer, and `items` is in **registry order**, which is the
 * order the screen renders them in.
 */
export const SettingsDetailsResponseSchema = z
  .object({
    items: z.array(SettingDetailSchema),
  })
  .strict();

export type SettingsDetailsResponse = z.infer<typeof SettingsDetailsResponseSchema>;
